import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { forkJoin, Observable } from 'rxjs';
import { Router } from '@angular/router';
import { FormLabelTranslationService } from '~shared/components/form-fields/translations/form-label-translations.service';
import { ButtonsTranslations } from '~shared/components/buttons/buttons-translations.service';
import { SpinnerService } from '~shared/components/buttons/spinner/spinner.service';
import { customerNumberDifferent } from '~shared/directives/form-validators';
import { SidebarService } from '~core/components/sidebar/sidebar.service';
import { CustomerOverviewTranslationsService, LoginTranslationsService } from '~shared/translations';
import { NotificationType } from '~core/enum';
import { Club, SidebarOptions } from '~core/models';
import { CustomerService, DefaultNotificationService, NavigationService, StorageService } from '~core/services';

/**
 * This component is responsible for the import of customers from CSV files.
 */
@UntilDestroy()
@Component({
  selector: 'app-merge-customer-select',
  templateUrl: 'merge-customer-select.component.html',
})

export class MergeCustomerSelectComponent implements OnInit, OnDestroy {
  public selectedClub: Club;
  public form: UntypedFormGroup;
  public spinnerName: string = 'customerMergerSubmit';

  constructor(
    public sidebarService: SidebarService,
    public navigationService: NavigationService,
    private formBuilder: UntypedFormBuilder,
    public storage: StorageService,
    public flTranslations: FormLabelTranslationService,
    public customerService: CustomerService,
    public buttonsTranslations: ButtonsTranslations,
    private spinnerService: SpinnerService,
    private notificationService: DefaultNotificationService,
    private customerTranslations: CustomerOverviewTranslationsService,
    private router: Router,
    private loginTranslationsService: LoginTranslationsService,
  ) {
    this.sidebarService.setSidebar(new SidebarOptions(false, false));
    this.storage.castSelectedClub.pipe(untilDestroyed(this))
      .subscribe(club => this.selectedClub = club);
    this.navigationService.setCurrentPage('customerMerger');
    this.navigationService.setCurrentBreadcrumbs(['crm', 'customerMerger']);
  }

  public ngOnInit(): void {
    if (!this.selectedClub.tixxCustomerMergeAllowed) {
      this.notificationService.showNotification({
        type: NotificationType.WARNING,
        message: this.loginTranslationsService.i18nErrorNoAccess,
      });
      this.router.navigate([`/club/${this.selectedClub.id}`]);
    }
    this.createForm();
  }

  public ngOnDestroy(): void { }

  /**
   * Creates the form and applies the validators.
   */
  private createForm(): void {
    this.form = this.formBuilder.group({
      firstCustomer: [null, [Validators.required, Validators.minLength(2)]],
      secondCustomer: [null, [Validators.required, Validators.minLength(2)]],
    }, { validators: customerNumberDifferent });
  }

  public submitForm(): void {
    this.spinnerService.show(this.spinnerName);
    const calls: Array<undefined | Observable<any>> = [
      this.customerService.advancedSearchBy(`externalNumber=${this.form.value.firstCustomer}`, this.selectedClub.id),
      this.customerService.advancedSearchBy(`externalNumber=${this.form.value.secondCustomer}`, this.selectedClub.id)
    ];

    forkJoin(calls).subscribe(([firstCustomer, secondCustomer]) => {
      if (firstCustomer.length && secondCustomer.length) {
        this.spinnerService.hide(this.spinnerName);
        this.router.navigate(['/club', this.selectedClub.id, 'merge-customer'], {
          queryParams: { firstCustomer: firstCustomer[0].id, secondCustomer: secondCustomer[0].id }
        });
      } else {
        this.notificationService.showNotification({
          type: NotificationType.ALERT,
          message: this.customerTranslations.i18nErrorCustomersNotExisting
        });
        this.spinnerService.hide(this.spinnerName);
      }
    });
  }
}
